import { useEffect } from 'react';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import useLunchStore from '../../store/useLunchStore';
import useRoomParentsStore from '../../store/useRoomParentsStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { DIET_TAGS, DAY_NAMES } from '../../utils/constants';
import { IconCheck, IconPlus } from '../../components/icons';

export default function LunchMenuPage() {
  const {
    menu,
    activeDay,
    setActiveDay,
    updateOptionText,
    toggleOptionTag,
    addOption,
    removeOption,
    loadDay,
    saveDay,
    dayLoading,
    saving,
  } = useLunchStore();
  const logActivity = useRoomParentsStore((s) => s.logActivity);
  const ctx = useContextStore(selectCurrentContext);
  const schoolId = ctx?.schoolId || null;

  /* Autofill from the API on page load and whenever the day pill changes. */
  useEffect(() => {
    loadDay(activeDay, schoolId);
  }, [activeDay, schoolId, loadDay]);

  const saveMenu = async () => {
    const ok = await saveDay(schoolId);
    if (!ok) return;
    showToast('Lunch menu updated — synced to every grade ✓');
    logActivity('Updated Lunch Menu', 'Made changes to the weekly menu');
  };

  const mealOptions = (meal) => {
    const opts = menu[activeDay][meal];
    return opts.map((opt, i) => (
      <div className="lunch-opt-row" key={opt.id}>
        <div className="opt-row-top">
          <div className="opt-num">{i + 1}</div>
          <input type="text" value={opt.text} onChange={(e) => updateOptionText(meal, opt.id, e.target.value)} />
          {opts.length > 1 && (
            <div className="opt-del" title="Remove option" onClick={() => removeOption(meal, opt.id)}>×</div>
          )}
        </div>
        <div className="diet-chip-row">
          {DIET_TAGS.map((t) => (
            <span
              key={t.id}
              className={`diet-chip ${opt.tags.includes(t.id) ? 'on' : ''}`}
              onClick={() => toggleOptionTag(meal, opt.id, t.id)}
            >
              {t.icon} {t.label}
            </span>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className="sec on">
      <SectionHead
        title="Weekly Lunch & Breakfast Menu"
        sub="This is the master menu for the whole school — it flows down to every grade's calendar automatically. Matches the food-selection UI from the parent app: each meal can have multiple options, each taggable (Vegetarian, Vegan, Pre-K, Gluten Free, Local Ingredients)."
        actions={
          <button className="btn btn-primary" disabled={saving || dayLoading} onClick={saveMenu}>
            {saving ? <><Spinner /> Saving…</> : <><IconCheck /> Save Menu</>}
          </button>
        }
      />

      <div className="daypicker">
        {Object.keys(menu).map((day) => (
          <div key={day} className={`daypill ${day === activeDay ? 'on' : ''}`} onClick={() => setActiveDay(day)}>
            {day}
          </div>
        ))}
      </div>

      <Card title={<>Breakfast Options — {DAY_NAMES[activeDay]}</>}>
        {dayLoading ? (
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
            <Spinner variant="green" /> Loading {DAY_NAMES[activeDay]}'s menu…
          </div>
        ) : (
          <>
            <div>{mealOptions('breakfast')}</div>
            <div className="add-opt-link" onClick={() => addOption('breakfast')}>
              <IconPlus />
              Add another option
            </div>
          </>
        )}
      </Card>

      <Card title={<>Lunch Options — {DAY_NAMES[activeDay]}</>}>
        {dayLoading ? (
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
            <Spinner variant="green" /> Loading {DAY_NAMES[activeDay]}'s menu…
          </div>
        ) : (
          <>
            <div>{mealOptions('lunch')}</div>
            <div className="add-opt-link" onClick={() => addOption('lunch')}>
              <IconPlus />
              Add another option
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
